class RevokeFormContainer {
  constructor($q, appErrorHandler, appSessionCapabilities, brands, users, permissions) {
    'ngInject';

    this.$q = $q;
    this.appErrorHandler = appErrorHandler;
    this.appSessionCapabilities = appSessionCapabilities;
    this.brands = brands;
    this.users = users;
    this.permissions = permissions;

    this.permissionsByBrand = {};
    this.sharedPermissions = [];
    this.scopes = [];
    this.scopesList = [];

    this.loading = false;
    this.selectedScope = {};

    this.autocompletes = {
      brands: {
        text: '',
        list: null,
        results: [],
        lastSearch: '',
      },
      users: {
        text: '',
        list: null,
        results: [],
        lastSearch: '',
      },
    };

    this.pillsVisibility = {
      brands: false,
      users: false,
    };

    this.fakeScope = {
      id: 'all',
      title: 'All of them',
    };

    this.brandIds = [];
    this.userIds = [];
    this.isBrandAdmin = false;
  }
  $onInit() {
    this.isBrandAdmin = this.appSessionCapabilities.isBrandAdmin(false);
    if (!this.showUsers) {
      this.autocompletes.brands.list = this.brands.newList();
    }

    if (!this.showBrands) {
      this.autocompletes.users.list = this.users.newList();
    }

    this._buildIdsList();
    this._loadScopes()
    .then(() => this._loadSelectedBrandsPermissions());
  }
  save() {
    let result;
    if (this.loading) {
      result = this.$q.reject(new Error('The permissions are already being revoked'));
    } else {
      const userIds = this.usersList.map((user) => user.id);
      const brandIds = this.brandsList.map((brand) => brand.id);

      this.loading = true;
      result = this.permissions.revoke(userIds, brandIds, this.selectedScope.id)
      .then(() => {
        this.onSave();
      })
      .catch((error) => {
        this.appErrorHandler.handle(error, 'There was an error while revoking the permissions');
      })
      .then(() => {
        this.loading = false;
      });
    }

    return result;
  }
  selectScope(scope) {
    if (scope.id !== this.selectedScope.id) {
      this.selectedScope = scope;
    }
  }
  autocompleteSearch(name, text) {
    const autocomplete = this.autocompletes[name];
    if (text === '') {
      autocomplete.lastSearch = '';
      autocomplete.results = [];
    } else if (text !== autocomplete.lastSearch) {
      autocomplete.lastSearch = text;
      autocomplete.results = [];
      const isForBrands = name === 'brands';
      autocomplete.results = isForBrands ?
        this._autocompleteBrands(autocomplete.lastSearch) :
        this._autocompleteUsers(autocomplete.lastSearch);
    }
  }
  autocompleteSelect(name, selection) {
    const autocomplete = this.autocompletes[name];
    autocomplete.lastSearch = '';
    autocomplete.results = [];
    const isForBrands = name === 'brands';
    if (isForBrands) {
      this._brandAutocompleteSelect(selection);
    } else {
      this._userAutocompleteSelect(selection);
    }
  }
  pillsToggleVisibility(name) {
    this.pillsVisibility[name] = !this.pillsVisibility[name];
  }
  pillsRemoveSelection(name, selection) {
    const isForBrands = name === 'brands';
    const target = isForBrands ? this.brandsList : this.usersList;
    const method = isForBrands ? this.onBrandsChange : this.onUsersChange;
    const index = target.findIndex((item) => item.id === selection.id);
    if (index > -1) {
      target.splice(index, 1);
      if (isForBrands && this.isBrandAdmin) {
        delete this.permissionsByBrand[selection.id];
        this._calculateSharedScopes();
        this._calculateSelections();
      }
      this._buildIdsList();
      method({ [name]: target });
    }
  }
  _buildIdsList() {
    this.brandIds = this.brandsList.map((brand) => brand.id);
    this.userIds = this.usersList.map((user) => user.id);
  }
  _loadScopes() {
    let result;
    if (this.loading) {
      result = this.$q.reject(new Error('The products are already being requested'));
    } else {
      this.loading = true;
      result = this.permissions.scopesWithPermissions()
      .then((scopes) => {
        this.scopes = scopes;
        if (!this.isBrandAdmin) {
          this.scopesList = scopes;
        }
      })
      .catch((error) => {
        this.appErrorHandler.handle(error, 'There was an error while loading the products');
      })
      .then(() => {
        this.loading = false;
      });
    }

    return result;
  }
  _autocompleteBrands(text) {
    const query = text.toLowerCase();
    return this.availableBrands.filter(({
      id,
      name,
      photorank_id: photorankId,
    }) => !this.brandIds.includes(id) && (
      name.toLowerCase().includes(query) ||
      photorankId.toString().toLowerCase().includes(query)
    ));
  }
  _autocompleteUsers(text) {
    return this.availableUsers.filter(({
      id,
      name,
      email,
    }) => !this.userIds.includes(id) && (
      name.toLowerCase().includes(text) ||
      email.toLowerCase().includes(text)
    ));
  }
  _loadSelectedBrandsPermissions() {
    return this.brandsList.length ?
      this._loadBrandPermissions(this.brandsList.map((brand) => brand.id)) :
      this.$q.resolve();
  }
  _loadBrandPermissions(brandIds) {
    let result;
    if (!this.brandsList.length || !this.isBrandAdmin) {
      this._calculateSharedScopes();
      result = this.$q.resolve();
    } else if (this.loading) {
      result = this.$q.reject(new Error('The permissions are already being requested'));
    } else {
      this.loading = true;
      result = this.brands.permissions(brandIds)
      .then((brands) => {
        Object.keys(brands).forEach((brandId) => {
          this.permissionsByBrand[brandId] = brands[brandId];
        });
        this._calculateSharedScopes();
      })
      .catch((error) => {
        this.appErrorHandler.handle(error, 'There was an error while loading the permissions');
      })
      .then(() => {
        this.loading = false;
      });
    }

    return result;
  }
  _calculateSharedPermissions() {
    const brandIds = Object.keys(this.permissionsByBrand);
    let sharedPermissions;
    if (brandIds.length) {
      const firstId = brandIds.shift();
      sharedPermissions = this.permissionsByBrand[firstId].map((permission) => permission.id);
      if (brandIds.length) {
        this.sharedPermissions = brandIds
        .reduce((shared, brandId) => (
          this.permissionsByBrand[brandId]
          .filter((permission) => shared.includes(permission.id))
          .map((permission) => permission.id)
        ), sharedPermissions);
      } else {
        this.sharedPermissions = sharedPermissions;
      }
    } else {
      this.sharedPermissions = [];
    }
  }
  _calculateSharedScopes() {
    this.scopesList = [];
    if (this.isBrandAdmin) {
      this._calculateSharedPermissions();
      this.scopesList = this.scopes
      .filter((scope) => (
        scope.permissions.some((permission) => this.sharedPermissions.includes(permission.id))
      ));
      this.scopesList.unshift(this.fakeScope);
    } else {
      this.scopesList = [this.fakeScope, ...this.scopes];
    }
  }
  _calculateSelections() {
    if (
      this.selectedScope.id &&
      !this.scopesList.find((scope) => scope.id === this.selectedScope.id)
    ) {
      this.selectedScope = {};
    }
  }
  _userAutocompleteSelect(selection) {
    const alreadyIn = this.usersList.find((user) => user.id === selection.id);
    if (!alreadyIn) {
      this.usersList.push(selection);
      this._buildIdsList();
      this.onUsersChange({ users: this.usersList });
    }
  }
  _brandAutocompleteSelect(selection) {
    const alreadyIn = this.brandsList.find((user) => user.id === selection.id);
    if (!alreadyIn) {
      this.brandsList.push(selection);
      this._buildIdsList();
      if (this.isBrandAdmin) {
        this._loadBrandPermissions([selection.id])
        .then(() => {
          this._calculateSelections();
          this.onBrandsChange({ brands: this.brandsList });
        });
      } else {
        this.onBrandsChange({ brands: this.brandsList });
      }
    }
  }
}

export default {
  controller: RevokeFormContainer,
  bindings: {
    onSave: '&',
    onCancel: '&',

    ownerLabel: '@',
    ownerCaption: '<',

    brandsList: '<',
    availableBrands: '<',
    showBrands: '<',
    onBrandsChange: '&',

    usersList: '<',
    showUsers: '<',
    availableUsers: '<',
    onUsersChange: '&',
  },

  template: `
    <revoke-form
      loading="$ctrl.loading"

      owner-label="{{$ctrl.ownerLabel}}"
      owner-caption="$ctrl.ownerCaption"

      on-save="$ctrl.save()"
      on-cancel="$ctrl.onCancel()"

      brands="$ctrl.brandsList"
      show-brands="$ctrl.showBrands"
      show-all-brands="$ctrl.pillsVisibility.brands"
      autocomplete-brands-results="$ctrl.autocompletes.brands.results"

      users="$ctrl.usersList"
      show-users="$ctrl.showUsers"
      show-all-users="$ctrl.pillsVisibility.users"
      autocomplete-users-results="$ctrl.autocompletes.users.results"

      scopes="$ctrl.scopesList"
      selected-scope="$ctrl.selectedScope"
      on-scope-selection="$ctrl.selectScope(scope)"

      on-autocomplete-search="$ctrl.autocompleteSearch(name, text)"
      on-autocomplete-selection="$ctrl.autocompleteSelect(name, selection)"

      on-toggle-pills-visibility="$ctrl.pillsToggleVisibility(name)"
      on-remove-pill="$ctrl.pillsRemoveSelection(name, selection)"
    ></revoke-form>
  `,
};
